import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  IndexNowStatus,
  IndexNowSubmit,
  ScInspection,
  ScMovers,
  ScQuota,
  ScReport,
  ScSetupResult,
  ScSetupStart,
  ScSetupStatus,
  ScSitemaps,
} from '../lib/types';
import { googleErrorText } from './google';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Arama performansı (F5-10): Search Console verisinin özeti, boyut tabloları, yükselen/düşen sayfalar, site
 * haritaları, URL denetimi ve haftalık e-posta özeti. Veriler günde bir senkronlanır (veya "Şimdi senkronize et").
 */

type Dim = 'query' | 'page' | 'country' | 'device';
const DIMS: Dim[] = ['query', 'page', 'country', 'device'];
/** Google ülke kodu (ISO alfa-3) → alfa-2 (ad gösterimi için); bilinmeyen kod olduğu gibi yazılır. */
const A3: Record<string, string> = {
  tur: 'TR',
  deu: 'DE',
  usa: 'US',
  gbr: 'GB',
  nld: 'NL',
  fra: 'FR',
  aze: 'AZ',
  cyp: 'CY',
  aut: 'AT',
  bel: 'BE',
  che: 'CH',
  ita: 'IT',
  esp: 'ES',
  swe: 'SE',
  dnk: 'DK',
  nor: 'NO',
  rus: 'RU',
  ukr: 'UA',
  grc: 'GR',
  bgr: 'BG',
  rou: 'RO',
  irq: 'IQ',
  irn: 'IR',
  sau: 'SA',
  are: 'AE',
  qat: 'QA',
  kaz: 'KZ',
  geo: 'GE',
  can: 'CA',
  aus: 'AU',
};

export function SearchConsolePage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  const [days, setDays] = useState(28);
  const [dim, setDim] = useState<Dim>('query');
  const [q, setQ] = useState('');
  const report = useQuery({
    queryKey: ['sc-report', storeId, days, dim, q],
    queryFn: () =>
      api.get<ScReport>(
        `/v1/admin/search-console/report?storeId=${storeId}&days=${days}&dimension=${dim}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: !!storeId,
  });
  const d = report.data;
  // Google'dan dönüş (F3B-28): "Google'a bağlan" ile başlatıldıysa adres ?kurulum=1&magaza=<id> ile gelir.
  const [autoRun, setAutoRun] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('kurulum') !== '1') return;
    const m = p.get('magaza');
    if (m) setStoreId(m);
    setAutoRun(true);
    window.history.replaceState(null, '', window.location.pathname);
  }, [setStoreId]);
  return (
    <PageFrame helpKey="page.search_console">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {storeId && (
        <SetupSection storeId={storeId} autoRun={autoRun} onAutoRunDone={() => setAutoRun(false)} />
      )}
      {storeId && <IndexNowSection storeId={storeId} />}
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.link ? (
        <EmptyState
          title={t('admin.sc.notLinked')}
          description={t('admin.sc.notLinkedLead')}
          action={
            <Link to="/google" className="text-pine underline underline-offset-2">
              {t('admin.nav.google')}
            </Link>
          }
        />
      ) : (
        <>
          <StatusSection storeId={storeId} d={d} />
          {d.totals && d.range ? (
            <>
              <Section title={t('admin.sc.summaryTitle')} action={<HelpTip k="sc.metrics" />}>
                <Field label={t('admin.sc.period')} className="max-w-xs">
                  {(p) => (
                    <Select {...p} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
                      {[7, 28, 90].map((n) => (
                        <option key={n} value={n}>
                          {t('admin.sc.lastDays', { count: n })}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Metrics d={d} />
                <Chart series={d.series} />
              </Section>
              <Section title={t('admin.sc.tableTitle')} action={<HelpTip k="sc.dimension" />}>
                <div className="flex flex-wrap items-end gap-3">
                  <Field label={t('admin.sc.dimension')} className="max-w-xs">
                    {(p) => (
                      <Select
                        {...p}
                        value={dim}
                        onChange={(e) => {
                          setDim(e.target.value as Dim);
                          setQ('');
                        }}
                      >
                        {DIMS.map((x) => (
                          <option key={x} value={x}>
                            {t(`admin.sc.dims.${x}`)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label={t('admin.sc.filter')} className="max-w-sm flex-1">
                    {(p) => <Input {...p} value={q} onChange={(e) => setQ(e.target.value)} maxLength={100} />}
                  </Field>
                </div>
                <Rows d={d} dim={dim} />
              </Section>
              <MoversSection storeId={storeId} />
            </>
          ) : (
            <p className="text-sm text-ink-2">{t('admin.sc.noData')}</p>
          )}
          <SitemapsSection storeId={storeId} />
          <InspectSection storeId={storeId} />
          <DigestSection />
        </>
      )}
    </PageFrame>
  );
}

function StatusSection({ storeId, d }: { storeId: string; d: ScReport }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const sync = useApiMutation(
    () => api.post<{ rows: number }>('/v1/admin/search-console/sync', { storeId }),
    {
      invalidate: [['sc-report'], ['sc-movers']],
      success: (r) => t('admin.sc.synced', { rows: r.rows }),
    },
  );
  const l = d.link!;
  return (
    <Section title={t('admin.sc.statusTitle')} action={<HelpTip k="sc.sync" />}>
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{l.property}</span>
        {l.lastError ? (
          <Badge tone="rose">{googleErrorText(t, l.lastError)}</Badge>
        ) : (
          <span className="text-ink-2">
            {l.lastSyncAt
              ? t('admin.sc.lastSync', { time: formatDate(l.lastSyncAt, true) })
              : t('admin.sc.neverSynced')}
          </span>
        )}
        {d.range && (
          <span className="text-ink-3">
            {t('admin.sc.dataRange', {
              from: formatDate(d.range.from, false),
              to: formatDate(d.range.to, false),
            })}
          </span>
        )}
      </p>
      {can('seo.write') && (
        <Button className="mt-3" loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
          {t('admin.sc.syncNow')}
        </Button>
      )}
    </Section>
  );
}

function useFormat() {
  const { locale } = useI18n();
  const tag = locale === 'tr' ? 'tr-TR' : locale === 'en' ? 'en-GB' : locale;
  const n = new Intl.NumberFormat(tag);
  const pctF = new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 1 });
  const dec = new Intl.NumberFormat(tag, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
  return {
    num: (v: number) => n.format(v),
    pct: (v: number) => pctF.format(v),
    pos: (v: number | null) => (v === null ? '—' : dec.format(v)),
  };
}

function Metrics({ d }: { d: ScReport }) {
  const t = useT();
  const f = useFormat();
  const cur = d.totals!;
  const prev = d.previous;
  const change = (a: number, b: number | undefined) =>
    b ? `${a >= b ? '+' : ''}${f.pct((a - b) / b)}` : null;
  const items: Array<[string, string, string | null]> = [
    ['clicks', f.num(cur.clicks), change(cur.clicks, prev?.clicks)],
    ['impressions', f.num(cur.impressions), change(cur.impressions, prev?.impressions)],
    ['ctr', f.pct(cur.ctr), null],
    ['position', f.pos(cur.position), null],
  ];
  return (
    <dl className="mt-4 grid gap-3 sm:grid-cols-4">
      {items.map(([k, v, c]) => (
        <div key={k} className="rounded-md border border-line px-3 py-2">
          <dt className="text-sm text-ink-2">{t(`admin.sc.metrics.${k}`)}</dt>
          <dd className="text-xl font-medium">{v}</dd>
          {c && <dd className="text-sm text-ink-3">{t('admin.sc.vsPrevious', { change: c })}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** Günlük tıklama çubuk grafiği (değerler tabloda da var; grafik yalnızca görsel özet). */
function Chart({ series }: { series: ScReport['series'] }) {
  const t = useT();
  const max = Math.max(1, ...series.map((s) => s.clicks));
  const w = 600;
  const h = 120;
  const bw = w / Math.max(series.length, 1);
  return (
    <figure className="mt-4">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-32 w-full" role="img" aria-label={t('admin.sc.chartLabel')}>
        {series.map((s, i) => {
          const bh = Math.max(1, (s.clicks / max) * (h - 10));
          return (
            <rect
              key={s.date}
              x={i * bw + 1}
              y={h - bh}
              width={Math.max(1, bw - 2)}
              height={bh}
              className="fill-pine"
            >
              <title>{`${s.date}: ${s.clicks}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="text-sm text-ink-3">{t('admin.sc.chartCaption')}</figcaption>
    </figure>
  );
}

function Rows({ d, dim }: { d: ScReport; dim: Dim }) {
  const t = useT();
  const f = useFormat();
  const { locale } = useI18n();
  const regions = new Intl.DisplayNames([locale], { type: 'region' });
  const label = (k: string) =>
    dim === 'country'
      ? A3[k]
        ? (regions.of(A3[k]) ?? k.toUpperCase())
        : k.toUpperCase()
      : dim === 'device'
        ? t(`admin.sc.devices.${k.toLowerCase()}`)
        : k;
  if (!d.rows.length) return <p className="mt-3 text-sm text-ink-2">{t('admin.sc.noRows')}</p>;
  return (
    <div className="mt-3 overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm" aria-label={t(`admin.sc.dims.${dim}`)}>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t(`admin.sc.dims.${dim}`)}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.sc.metrics.clicks')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.sc.metrics.impressions')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.sc.metrics.ctr')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.sc.metrics.position')}</th>
          </tr>
        </thead>
        <tbody>
          {d.rows.map((r) => (
            <tr key={r.key} className="border-t border-line">
              <td className="px-3 py-2 break-all">{label(r.key)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.num(r.clicks)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.num(r.impressions)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.pct(r.ctr)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{f.pos(r.position)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MoversSection({ storeId }: { storeId: string }) {
  const t = useT();
  const [days, setDays] = useState(28);
  const q = useQuery({
    queryKey: ['sc-movers', storeId, days],
    queryFn: () => api.get<ScMovers>(`/v1/admin/search-console/movers?storeId=${storeId}&days=${days}`),
  });
  const list = (items: ScMovers['rising'], kind: 'rising' | 'falling') => (
    <div>
      <h3 className="mb-2 font-medium">{t(`admin.sc.${kind}`)}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-ink-3">{t('admin.sc.noMovers')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {items.map((m) => (
            <li key={m.key} className="flex justify-between gap-3">
              <span className="min-w-0 break-all">{m.key}</span>
              <span className={`shrink-0 tabular-nums ${kind === 'rising' ? 'text-moss' : 'text-rose'}`}>
                {m.change > 0 ? `+${m.change}` : m.change}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <Section
      title={t('admin.sc.moversTitle')}
      description={t('admin.sc.moversLead')}
      action={<HelpTip k="sc.movers" />}
    >
      <Field label={t('admin.sc.period')} className="max-w-xs">
        {(p) => (
          <Select {...p} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 28].map((n) => (
              <option key={n} value={n}>
                {t('admin.sc.lastDays', { count: n })}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.comparable ? (
        <p className="mt-3 text-sm text-ink-2">{t('admin.sc.notComparable')}</p>
      ) : (
        <div className="mt-3 grid gap-6 md:grid-cols-2">
          {list(q.data.rising, 'rising')}
          {list(q.data.falling, 'falling')}
        </div>
      )}
    </Section>
  );
}

function SitemapsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['sc-sitemaps', storeId],
    queryFn: () => api.get<ScSitemaps>(`/v1/admin/search-console/sitemaps?storeId=${storeId}`),
    retry: false,
  });
  const submit = useApiMutation(() => api.post('/v1/admin/search-console/sitemaps', { storeId }), {
    invalidate: [['sc-sitemaps', storeId]],
    success: t('admin.sc.sitemapSubmitted'),
  });
  const s = q.data;
  return (
    <Section
      title={t('admin.sc.sitemapsTitle')}
      description={t('admin.sc.sitemapsLead')}
      action={<HelpTip k="sc.sitemaps" />}
    >
      {q.isError ? (
        <p className="text-sm text-rose">{t('admin.sc.sitemapsError')}</p>
      ) : !s ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          {s.sitemaps.length === 0 ? (
            <p className="text-sm text-ink-2">{t('admin.sc.noSitemaps')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.sc.sitemapsTitle')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.sc.colSitemap')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.sc.colSubmitted')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.sc.colStatus')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.sc.colUrls')}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.sitemaps.map((m) => (
                    <tr key={m.path} className="border-t border-line">
                      <td className="px-3 py-2 break-all">{m.path}</td>
                      <td className="px-3 py-2">{formatDate(m.lastSubmitted, true)}</td>
                      <td className="px-3 py-2">
                        <Badge tone={m.errors ? 'rose' : m.isPending ? 'amber' : 'moss'}>
                          {m.errors
                            ? t('admin.sc.sitemapErrors', { count: m.errors })
                            : m.isPending
                              ? t('admin.sc.sitemapPending')
                              : t('admin.sc.sitemapOk')}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">{m.submitted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s.ownSitemap && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-2">{t('admin.sc.ownSitemap')}</span>
              <code className="break-all">{s.ownSitemap}</code>
              {!s.ownInProperty && <Badge tone="amber">{t('admin.sc.notInProperty')}</Badge>}
            </p>
          )}
          {can('seo.write') && s.ownSitemap && s.ownInProperty && (
            <span className="mt-3 flex items-center gap-2">
              <Button loading={submit.isPending} onClick={() => submit.mutate(undefined)}>
                {t('admin.sc.submitSitemap')}
              </Button>
              <HelpTip k="sc.sitemap_submit" />
            </span>
          )}
        </>
      )}
    </Section>
  );
}

function InspectSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<ScInspection | null>(null);
  const quota = useQuery({
    queryKey: ['sc-quota', storeId],
    queryFn: () => api.get<ScQuota>(`/v1/admin/search-console/inspection-quota?storeId=${storeId}`),
  });
  const inspect = useApiMutation(
    () => api.post<ScInspection>('/v1/admin/search-console/inspect', { storeId, url: url.trim() }),
    { invalidate: [['sc-quota', storeId]], onSuccess: (r) => setResult(r), silentFieldErrors: true },
  );
  const rows: Array<[string, string | null]> = result
    ? [
        ['verdict', result.verdict ? t(`admin.sc.verdicts.${result.verdict}`) : null],
        ['coverageState', result.coverageState],
        ['indexingState', result.indexingState],
        ['robotsTxtState', result.robotsTxtState],
        ['pageFetchState', result.pageFetchState],
        ['lastCrawlTime', result.lastCrawlTime ? formatDate(result.lastCrawlTime, true) : null],
        ['googleCanonical', result.googleCanonical],
      ]
    : [];
  return (
    <Section
      title={t('admin.sc.inspectTitle')}
      description={t('admin.sc.inspectLead')}
      action={<HelpTip k="sc.inspect" />}
    >
      {can('seo.write') && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            inspect.mutate(undefined);
          }}
        >
          <Field
            label={t('admin.sc.inspectUrl')}
            error={fieldError(inspect.error, 'url')}
            className="min-w-72 flex-1"
          >
            {(p) => (
              <Input
                {...p}
                type="url"
                inputMode="url"
                value={url}
                placeholder="https://"
                onChange={(e) => setUrl(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" loading={inspect.isPending} disabled={!url.trim()}>
            {t('admin.sc.inspect')}
          </Button>
        </form>
      )}
      {quota.data && (
        <p className="mt-2 text-sm text-ink-3">
          {t('admin.sc.quota', { used: quota.data.used, limit: quota.data.limit })}
        </p>
      )}
      {result && (
        <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]" aria-live="polite">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-ink-2">{t(`admin.sc.inspectFields.${k}`)}</dt>
              <dd className="break-all">{v ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}
    </Section>
  );
}

function DigestSection() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['sc-digest'],
    queryFn: () => api.get<{ enabled: boolean }>('/v1/admin/search-console/digest'),
  });
  const save = useApiMutation((enabled: boolean) => api.put('/v1/admin/search-console/digest', { enabled }), {
    invalidate: [['sc-digest']],
    success: t('admin.sc.digestSaved'),
  });
  if (!q.data) return null;
  return (
    <Section
      title={t('admin.sc.digestTitle')}
      description={t('admin.sc.digestLead')}
      action={<HelpTip k="sc.digest" />}
    >
      <span className="flex items-center gap-2">
        <Switch
          checked={q.data.enabled}
          disabled={!can('seo.write') || save.isPending}
          onCheckedChange={(v) => save.mutate(v)}
          label={t('admin.sc.digestToggle')}
        />
        <span className="text-sm">{t('admin.sc.digestToggle')}</span>
      </span>
    </Section>
  );
}

/**
 * Search Console kurulumu (F3B-28): tek düğme. Bağlı değilse veya Site Verification izni yoksa Google'a yönlendirir
 * (mevcut izinler korunur); dönüşte (veya bağlıysa hemen) sistem mülkleri dosya yöntemiyle doğrular, ekler, mağazayı
 * bağlar, site haritasını gönderir ve eski mülklerin sayfalarını eşler. Sonuçlar mülk mülk görünür; günlük URL denetimi
 * sonuçları altta.
 */
function SetupSection({
  storeId,
  autoRun,
  onAutoRunDone,
}: {
  storeId: string;
  autoRun: boolean;
  onAutoRunDone: () => void;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('org.integration.manage');
  const [idem] = useState(() => new IdempotencyKey());
  const status = useQuery({
    queryKey: ['sc-setup', storeId],
    queryFn: () => api.get<ScSetupStatus>(`/v1/admin/search-console/setup?storeId=${storeId}`),
  });
  const run = useApiMutation(
    () => api.post<ScSetupResult>('/v1/admin/search-console/setup', { storeId }, { idempotencyKey: idem }),
    {
      invalidate: [
        ['sc-setup', storeId],
        ['sc-report', storeId],
        ['not-found', storeId],
        ['redirects', storeId],
      ],
      success: t('admin.scSetup.done'),
    },
  );
  const connect = useApiMutation(
    () => api.post<ScSetupStart>('/v1/admin/search-console/setup/connect', { storeId }),
    {
      onSuccess: (r) => {
        if (r.ready) run.mutate(undefined);
        else window.location.assign(r.url);
      },
    },
  );
  const started = useRef(false);
  useEffect(() => {
    if (!autoRun || started.current || !status.data?.connection) return;
    started.current = true;
    onAutoRunDone();
    if (!status.data.connection.missingScopes.length) run.mutate(undefined);
  }, [autoRun, status.data, run, onAutoRunDone]);
  const d = status.data;
  const last = run.data ?? d?.lastRun ?? null;
  const conn = d?.connection;
  const needsConnect = !conn || conn.status !== 'active' || conn.missingScopes.length > 0;
  const stepText = (s: ScSetupResult['sites'][number]) =>
    s.added
      ? t('admin.scSetup.siteOk')
      : s.verified
        ? t('admin.scSetup.siteVerifiedOnly')
        : s.error
          ? t('admin.scSetup.siteFailed', {
              step: t(`admin.scSetup.steps.${s.error.step}`),
              reason: googleErrorText(t, s.error.code),
            })
          : t('admin.scSetup.sitePending');
  return (
    <Section
      title={t('admin.scSetup.title')}
      description={t('admin.scSetup.lead')}
      action={<HelpTip k="sc_setup.connect" />}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-ink-2">
            {conn
              ? t('admin.scSetup.connectedAs', { email: conn.accountEmail })
              : t('admin.scSetup.notConnected')}
            {conn && conn.missingScopes.length > 0 && ` · ${t('admin.scSetup.missingScope')}`}
            {conn && conn.status !== 'active' && conn.lastError && ` · ${googleErrorText(t, conn.lastError)}`}
          </p>
          {manage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                loading={connect.isPending || run.isPending}
                onClick={() => connect.mutate(undefined)}
              >
                {needsConnect
                  ? t('admin.scSetup.connect')
                  : last
                    ? t('admin.scSetup.rerun')
                    : t('admin.scSetup.run')}
              </Button>
              <HelpTip k="sc_setup.run" />
            </div>
          )}
          {d.sites.length > 0 && (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {d.sites.map((site) => {
                const r = last?.sites.find((x) => x.siteUrl === site.siteUrl);
                return (
                  <li key={site.siteUrl} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2">
                    <span className="min-w-0 flex-1 break-all font-medium">{site.siteUrl}</span>
                    <Badge tone="neutral">{t(`admin.scSetup.roles.${site.role}`)}</Badge>
                    <span className={r?.added ? 'text-moss' : r?.error ? 'text-rose' : 'text-ink-3'}>
                      {r ? stepText(r) : t('admin.scSetup.sitePending')}
                    </span>
                    {r?.error?.code === 'api_not_enabled' && <EnableApiLink step={r.error.step} />}
                  </li>
                );
              })}
            </ul>
          )}
          {last && (
            <div className="space-y-1 text-ink-2">
              <p>{t('admin.scSetup.lastRun', { date: formatDate(last.ranAt, true) })}</p>
              <p>
                {last.sitemap.submitted
                  ? t('admin.scSetup.sitemapOk', { url: last.sitemap.submitted })
                  : last.sitemap.error
                    ? t('admin.scSetup.sitemapFailed', { reason: googleErrorText(t, last.sitemap.error) })
                    : t('admin.scSetup.sitemapSkipped')}
              </p>
              <p>
                {t('admin.scSetup.legacyPages', {
                  found: last.legacyPages.found,
                  matched: last.legacyPages.matched,
                  table: last.legacyPages.table,
                  fallback: last.legacyPages.fallback,
                })}
                {last.legacyPages.error && ` · ${googleErrorText(t, last.legacyPages.error)}`}
              </p>
            </div>
          )}
          <p className="rounded-sm bg-surface-2 p-3 text-ink-2">{t('admin.scSetup.publishNote')}</p>
          <InspectionsTable rows={d.inspections} />
        </div>
      )}
    </Section>
  );
}

/** Günlük URL denetimi sonuçları (işçi her sabah; ürün sayfaları ve örnek eski adresler). */
function InspectionsTable({ rows }: { rows: ScSetupStatus['inspections'] }) {
  const t = useT();
  const { formatDate } = useI18n();
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-ink">{t('admin.scSetup.inspectionsTitle')}</h3>
        <HelpTip k="sc_setup.inspections" />
      </div>
      {!rows.length ? (
        <p className="text-ink-3">{t('admin.scSetup.inspectionsEmpty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('admin.scSetup.colUrl')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('admin.scSetup.colKind')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('admin.scSetup.colState')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('admin.scSetup.colCrawl')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="break-all px-3 py-2">{r.url}</td>
                  <td className="px-3 py-2">{t(`admin.scSetup.kinds.${r.kind}`)}</td>
                  <td className="px-3 py-2">
                    {r.error
                      ? googleErrorText(t, r.error)
                      : [r.verdict, r.coverageState].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.lastCrawlTime ? formatDate(r.lastCrawlTime, true) : '—'}
                    <span className="block text-xs text-ink-3">
                      {t('admin.scSetup.checkedAt', { date: formatDate(r.checkedAt, true) })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** IndexNow (F3B-29): Bing ve Yandex'e değişen adreslerin bildirimi; ilk çalışmada tümü, sonra on dakikada bir. */
function IndexNowSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [idem] = useState(() => new IdempotencyKey());
  const q = useQuery({
    queryKey: ['indexnow', storeId],
    queryFn: () => api.get<IndexNowStatus>(`/v1/admin/indexnow?storeId=${storeId}`),
  });
  const submit = useApiMutation(
    () => api.post<IndexNowSubmit>('/v1/admin/indexnow/submit', { storeId }, { idempotencyKey: idem }),
    {
      invalidate: [['indexnow', storeId]],
      onSuccess: (r) => {
        if (r.status === 'sent') toast.success(t('admin.indexNow.sent', { count: r.count }));
        else if (r.status === 'failed') toast.error(t('admin.indexNow.failed', { reason: r.error }));
        else toast.message(t(`admin.indexNow.skipped.${r.reason}`));
      },
    },
  );
  const d = q.data;
  return (
    <Section
      title={t('admin.indexNow.title')}
      description={t('admin.indexNow.lead')}
      action={<HelpTip k="indexnow.section" />}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-2 text-sm text-ink-2">
          {!d.live ? (
            <p>{t('admin.indexNow.notLive')}</p>
          ) : !d.indexing ? (
            <p>{t('admin.indexNow.indexingOff')}</p>
          ) : null}
          <p>{d.key ? t('admin.indexNow.key', { key: d.key }) : t('admin.indexNow.noKey')}</p>
          <p>
            {d.lastSubmittedAt
              ? t('admin.indexNow.last', { date: formatDate(d.lastSubmittedAt, true), count: d.lastCount })
              : t('admin.indexNow.never')}
          </p>
          {d.lastError && <p className="text-rose">{t('admin.indexNow.failed', { reason: d.lastError })}</p>}
          {can('seo.write') && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                loading={submit.isPending}
                disabled={!d.live || !d.indexing}
                onClick={() => submit.mutate(undefined)}
              >
                {t('admin.indexNow.submit')}
              </Button>
              <HelpTip k="indexnow.submit" />
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * Cloud projesinde kapalı API (F3B-28): adımın kullandığı API adı ve Google Cloud'daki etkinleştirme sayfası. Doğrulama
 * adımları Site Verification API'yi, ekleme/site haritası/eski sayfalar Search Console API'yi kullanır.
 */
const SC_API_OF_STEP: Record<string, { id: string; name: string }> = {
  token: { id: 'siteverification.googleapis.com', name: 'Google Site Verification API' },
  verify: { id: 'siteverification.googleapis.com', name: 'Google Site Verification API' },
};
const SEARCH_CONSOLE_API = { id: 'searchconsole.googleapis.com', name: 'Google Search Console API' };

function EnableApiLink({ step }: { step: string }) {
  const t = useT();
  const api = SC_API_OF_STEP[step] ?? SEARCH_CONSOLE_API;
  return (
    <p className="basis-full text-sm">
      {t('admin.scSetup.enableApi', { api: api.name })}{' '}
      <a
        href={`https://console.cloud.google.com/apis/library/${api.id}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-pine underline underline-offset-2"
      >
        {t('admin.scSetup.enableApiLink')}
      </a>{' '}
      <HelpTip k="sc_setup.enable_api" />
    </p>
  );
}
